import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { CATALOG_URLS, TASKING_URLS } from 'lib/api/endpoints';

// Each order type lives in its own table with its own endpoint.
// Pass orderType='catalog' or 'tasking' to hit a single endpoint;
// pass 'all' to fetch both in parallel and merge (used for admin "all" view
// and the drafts view, since drafts can be either type).
const flatten = (data) => (Array.isArray(data) ? data : data?.items ?? []);

// Tag rows with the order_type of the endpoint that produced them — the column
// itself was dropped from the DB so the FE attaches it for downstream filtering.
const tag = (rows, order_type) => rows.map((r) => ({ ...r, order_type }));

// page_size=200 matches the BE's max clamp so the list isn't silently truncated
// to the BE's default 20 (which used to hide orders past row 20). Until we have
// real pagination UI, this is the stop-gap that keeps the tab's row count in
// sync with the DB for any realistic user.
const DEFAULT_PAGE_SIZE = 200;

const fetchByType = async (orderType, params) => {
  const fullParams = { page_size: DEFAULT_PAGE_SIZE, ...params };
  if (orderType === 'all') {
    const [catalogResp, taskingResp] = await Promise.all([
      apiClient.get(CATALOG_URLS.getAllOrders, { params: fullParams }).catch(() => ({ data: { data: [] } })),
      apiClient.get(TASKING_URLS.getAllOrders, { params: fullParams }).catch(() => ({ data: { data: [] } })),
    ]);
    return [
      ...tag(flatten(catalogResp.data?.data ?? []), 'catalog'),
      ...tag(flatten(taskingResp.data?.data ?? []), 'tasking'),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
  const url = orderType === 'tasking' ? TASKING_URLS.getAllOrders : CATALOG_URLS.getAllOrders;
  const resp = await apiClient.get(url, { params: fullParams });
  return tag(flatten(resp.data?.data ?? []), orderType);
};

const urlsFor = (orderType) =>
  orderType === 'tasking' ? TASKING_URLS : CATALOG_URLS;

// Catalog & tasking tabs are server-side paginated. Accepts either a bare
// orderType string (legacy) or an options object {orderType, page, page_size,
// sort_by, sort_dir}. Returns the rows plus pagination metadata so the UI can
// drive a real pager.
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (arg = 'catalog', thunkAPI) => {
    const opts = typeof arg === 'string' ? { orderType: arg } : (arg || {});
    const { orderType = 'catalog', page = 1, page_size = 20, sort_by, sort_dir } = opts;
    try {
      const url = orderType === 'tasking' ? TASKING_URLS.getAllOrders : CATALOG_URLS.getAllOrders;
      // Drafts have their own tab/thunk — exclude them so the page budget isn't
      // spent on draft rows that this tab never shows.
      const params = { page, page_size, status_not_in: 'draft' };
      if (sort_by) params.sort_by = sort_by;
      if (sort_dir) params.sort_dir = sort_dir;
      const resp = await apiClient.get(url, { params });
      const rows = tag(flatten(resp.data?.data ?? []), orderType);
      return {
        orders: rows,
        total: resp.data?.total ?? rows.length,
        page: resp.data?.page ?? page,
        pageSize: resp.data?.page_size ?? page_size,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch orders');
    }
  }
);

// Fetch a single order by id for the detail view when it isn't on the currently
// loaded paginated page — e.g. deep-linking to /orders?id=<uuid> or clicking the
// existing-order link in the duplicate-order checkout error. The list endpoint
// returns the bare order object (not a list) when given an order_id param.
export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async ({ orderId, orderType = 'catalog' }, thunkAPI) => {
    const getOne = async (type) => {
      const resp = await apiClient.get(urlsFor(type).getAllOrders, {
        params: { order_id: orderId },
      });
      const data = resp.data?.data;
      const order = Array.isArray(data) ? data[0] : data;
      return order ? { ...order, order_type: type } : null;
    };
    try {
      if (orderType === 'catalog' || orderType === 'tasking') {
        const order = await getOne(orderType);
        return order ?? thunkAPI.rejectWithValue('Order not found');
      }
      // 'all' (or unknown): the id may belong to either table — try both.
      const order =
        (await getOne('catalog').catch(() => null)) ||
        (await getOne('tasking').catch(() => null));
      return order ?? thunkAPI.rejectWithValue('Order not found');
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch order'
      );
    }
  }
);

export const fetchAllOrders = createAsyncThunk(
  'orders/fetchAllOrders',
  async (_, thunkAPI) => {
    try {
      return await fetchByType('all', { status_not_in: 'draft' });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch all orders'
      );
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ orderId, status, orderType = 'catalog', message = null, updates = {} }, thunkAPI) => {
    try {
      await apiClient.put(urlsFor(orderType).updateOrder(orderId), {
        status,
        message,
        ...updates,
      });
      return { orderId, status };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to update order status'
      );
    }
  }
);

export const fetchDrafts = createAsyncThunk(
  'orders/fetchDrafts',
  async (_, thunkAPI) => {
    try {
      return await fetchByType('all', { status: 'draft' });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch drafts'
      );
    }
  }
);

export const deleteDraft = createAsyncThunk(
  'orders/deleteDraft',
  async ({ orderId, orderType = 'catalog' }, thunkAPI) => {
    try {
      await apiClient.delete(urlsFor(orderType).deleteOrder(orderId));
      return orderId;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to delete draft'
      );
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    orders: [],
    ordersTotal: 0,
    ordersPage: 1,
    ordersPageSize: 20,
    // Single order fetched by id for the detail view when it's off the loaded page.
    selectedOrderDetail: null,
    allOrders: [],
    loading: false,
    error: null,
    dropdownValue: 'tasking',
    searchTerm: '',
    drafts: [],
    draftsLoading: false,
    deletingDraftId: null,
  },
  reducers: {
    setDropdownValue(state, action) {
      state.dropdownValue = action.payload;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    clearOrderError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        const p = action.payload || {};
        state.orders = Array.isArray(p.orders) ? p.orders : [];
        state.ordersTotal = p.total ?? state.orders.length;
        state.ordersPage = p.page ?? 1;
        state.ordersPageSize = p.pageSize ?? 20;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.allOrders = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.selectedOrderDetail = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state) => {
        state.selectedOrderDetail = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const { orderId, status } = action.payload;
        const order = state.orders.find((o) => o.id === orderId);
        if (order) order.status = status;
        const adminOrder = state.allOrders.find((o) => o.id === orderId);
        if (adminOrder) adminOrder.status = status;
      })
      .addCase(updateOrderStatus.rejected, () => {
        // Intentionally do NOT set the page-level `error` here. A failed status
        // update is a transient mutation error (e.g. a duplicate-name 409 when
        // placing a draft) and is surfaced by the caller — inline in the modal
        // or via a toast. Setting `error` would trip OrderPage's full-page
        // error screen and blank the whole page (unmounting the modal).
      })
      .addCase(fetchDrafts.pending, (state) => {
        state.draftsLoading = true;
      })
      .addCase(fetchDrafts.fulfilled, (state, action) => {
        state.draftsLoading = false;
        state.drafts = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchDrafts.rejected, (state, action) => {
        state.draftsLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteDraft.pending, (state, action) => {
        state.deletingDraftId = action.meta.arg?.orderId;
      })
      .addCase(deleteDraft.fulfilled, (state, action) => {
        state.deletingDraftId = null;
        state.drafts = state.drafts.filter((d) => d.id !== action.payload);
        state.orders = state.orders.filter((o) => o.id !== action.payload);
        state.allOrders = state.allOrders.filter((o) => o.id !== action.payload);
      })
      .addCase(deleteDraft.rejected, (state, action) => {
        state.deletingDraftId = null;
        state.error = action.payload;
      });
  },
});

export const { setDropdownValue, setSearchTerm, clearOrderError } = orderSlice.actions;
export default orderSlice.reducer;
